import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import CandidateProfile from '../CandidateComponents/CandidateProfile';
import CandidateEvals from '../CandidateComponents/CandidateEvals';
import CandidateReport from '../CandidateComponents/CandidateReport';
import CandidateStatus from '../CandidateComponents/Utils/CandidateStatus';
import { API_ENDPOINTS, buildUrl } from '../Utils/ApiConfig';
import Navigation from './Components/Navigation';
import toast from 'react-hot-toast';

const CandidateDetails = () => {
  // Basic state variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Dedicated state variables for each data type
  const [candidateData, setCandidateData] = useState(null);
  const [candidateProfileJSON, setCandidateProfileJSON] = useState(null);

  const [jobTitle, setJobTitle] = useState(null);
  const [isCallbackLoading, setIsCallbackLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useSession();

  // Extract candidate ID from URL query parameters
  const getCandidateId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('cid');
  };

  /**
   * Handles API errors, especially 401 unauthorized errors
   * @param {Error} err - The error object
   * @returns {boolean} - Whether the error was handled
   */
  const handleApiError = (err) => {
    console.error('API Error:', err);
    console.log('Error status code:', err.status);
    
    // If it's a 401 error, it's an expired session - log out and redirect to login
    if (err.status === 401) {
      console.log('Session expired or unauthorized. Redirecting to login page.');
      logout();
      navigate('/login');
      return true; // Error was handled
    }
    
    return false; // Error needs further handling
  };

  useEffect(() => {
    const fetchCandidateDetails = async () => {
      const candidateId = getCandidateId();
      
      if (!candidateId) {
        setError('Candidate ID is missing');
        setLoading(false);
        return;
      }

      try {
        // Check if user exists
        if (!user) {
          throw new Error('User session not available. Please log in again.');
        }
        
        // Create URL with candidate ID parameter
        const url = new URL(API_ENDPOINTS.GET_FULL_CANDIDATE_DATA);
        url.searchParams.append('cid', candidateId);
        
        // Fetch candidate data with authorization header
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          }
        });
        
        if (!response.ok) {
          const error = new Error(`Error: ${response.status} ${response.statusText}`);
          error.status = response.status; // Add status to the error object for use in catch block
          throw error;
        }
        
        const responseData = await response.json();
        console.log('API Response:', responseData);
        
        // Based on the feedback, the response is an array with a single item
        if (Array.isArray(responseData) && responseData.length > 0) {
          const data = responseData[0]; // Get the first item in the array
          
          // Store the basic candidate data
          setCandidateData({
            candidate_id: candidateId,
            ...data
          });
          
          // Extract and set candidateProfileJSON
          if (data.talent.talentProfileJSON) {
            try {
              setCandidateProfileJSON(data.talent.talentProfileJSON);
              console.log('Candidate Profile JSON loaded:', data.talent.talentProfileJSON);
            } catch (err) {
              console.error('Error setting candidateProfileJSON:', err);
            }
          }
                  
          // Set job title from jobs.job_name
          if (data.jobs && data.jobs.job_name) {
            setJobTitle(data.jobs.job_name);
            console.log("Job Title from jobs:", data.jobs.job_name);
          }
          
        } else {
          console.warn('Unexpected API response format:', responseData);
          setError('Unexpected response format from the server');
        }
      } catch (err) {
        // Use the helper function to handle common API errors
        if (!handleApiError(err)) {
          // If the error wasn't handled by the helper, update the state with the error
          console.error('Error fetching candidate data:', err);
          setError(`Failed to load candidate data: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateDetails();
  }, [location.search]);
  
  // Check for hash fragment in URL and set active tab accordingly
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['profile', 'evaluations', 'report'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);
  
  // Set document title to candidate name
  useEffect(() => {
    if (candidateData) {
      document.title = "Candidate | " + candidateData.talent.fname + " " + candidateData.talent.lname;
    } else {
      document.title = "Candidate Details";
    }
    
    // Reset title when component unmounts
    return () => {
      document.title = "VaaS Admin";
    };
  }, [candidateData]);

  
  const handleTabChange = (tabName) => {
    // First update the active tab
    setActiveTab(tabName);
    
    // Update URL with hash fragment
    navigate(`${location.pathname}${location.search}#${tabName}`, { replace: true });
    
    // Scroll the window to the top
    window.scrollTo(0, 0);
  };

  // Handle callback trigger
  const handleCallbackTrigger = async () => {
    const candidateId = getCandidateId();
    if (!candidateId) return;

    const confirmed = window.confirm("Are you sure you want to trigger the Candidate Assessment callback?");
    if (!confirmed) return;

    setIsCallbackLoading(true);
    
    try {
      const response = await fetch(API_ENDPOINTS.CALL_CALLBACK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify({
          candidate_id: candidateId, // I DON'T THINK WE NEED THIS FOR ANYTHING, BUT FUCK IT SEND IT ANYWAYS BECAUSE ITS A CANDIDATE
          talent_id: candidateData?.talent?.talent_id,
          uid: user.id
        })
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      toast.success('Callback triggered successfully! Please note that this only sends the last created/assigned evaluation.');
    } catch (err) {
      console.error('Error triggering callback:', err);
      if (!handleApiError(err)) {
        toast.error('Failed to trigger callback. Please try again.');
      }
    } finally {
      setIsCallbackLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]"> {/* Added top padding to account for fixed header */}
      <Navigation 
        title={
          loading ? (
            <span className="inline-flex items-center">
              <span className="animate-pulse">Loading candidate details...</span>
            </span>
          ) : candidateData ? (
            <span>
              {candidateData.talent.fname} {candidateData.talent.lname}
              <span className="font-normal">
                {jobTitle ? ` applying to `: ''}<span className="font-semibold">{jobTitle || ''} </span>
                at <span className="font-semibold">{candidateData.jobs.company_name}</span>
              </span>
            </span>
          ) : (
            "Candidate Details"
          )
        } 
      />
      
      <div className="bg-white rounded-lg shadow relative">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading candidate details...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <p className="text-gray-600 mt-2">Please try again or go back to the job details.</p>
          </div>
        ) : (
          <div>
            {/* Tabs Navigation - Made sticky */}
            <div className="sticky top-[76px] z-30 bg-white border-b border-gray-200">
              <div className="flex justify-between items-center">
                <nav className="flex -mb-px overflow-x-auto">
                <button
                  onClick={() => handleTabChange('profile')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'profile'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Candidate Profile
                </button>
                <button
                  onClick={() => handleTabChange('evaluations')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'evaluations'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Candidate Evaluations
                </button>
                <button
                  onClick={() => handleTabChange('report')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'report'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Candidate Report
                </button>

                </nav>
                <div className="flex flex-wrap gap-2 items-center pr-4">
                  {candidateData?.talent?.talent_id && (
                    <a
                      href={`/talentdetails?tid=${candidateData.talent.talent_id}`}
                      className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors inline-block"
                    >
                      Talent Page
                    </a>
                  )}
                  <div className="flex flex-wrap items-center">
                    {getCandidateId() && (
                      <span className="text-xs text-gray-500">
                        Candidate ID: {getCandidateId()}
                      </span>
                    )}
                  </div>
                  {candidateData && candidateData.candidate_status && (
                    <div className="ml-auto flex items-center gap-2">
                      <CandidateStatus status={candidateData.candidate_status} showDot={true} size="sm" />
                      <button
                        onClick={handleCallbackTrigger}
                        disabled={isCallbackLoading}
                        className="p-1 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Trigger callback"
                      >
                        {isCallbackLoading ? (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Tab Content - Using key to force remount when tab changes */}
            <div className="p-6" key={activeTab}>
              {activeTab === 'profile' && (
                <CandidateProfile profileData={candidateProfileJSON} candidate={candidateData} />
              )}
              
              {activeTab === 'evaluations' && (
                <CandidateEvals candidate={candidateData} />
              )}
              
              {activeTab === 'report' && (
                <CandidateReport candidate={candidateData} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDetails;
